import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Check, X, Tag } from 'lucide-react';
import { getAdminCategories, createCategory, updateCategory, deleteCategory } from '../utils/api';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';

const emptyForm = { name: '', description: '', icon: '', is_active: true, sort_order: 0 };

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await getAdminCategories();
      setCategories(res.data.data || []);
    } catch { toast.error('Failed to load categories'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editId) {
        const res = await updateCategory(editId, form);
        setCategories(prev => prev.map(c => c.id === editId ? res.data.data : c));
        toast.success('Category updated');
      } else {
        const res = await createCategory(form);
        setCategories(prev => [...prev, res.data.data]);
        toast.success('Category created');
      }
      setEditId(null);
      setShowAdd(false);
      setForm(emptyForm);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (cat) => {
    setEditId(cat.id);
    setForm({ name: cat.name, description: cat.description || '', icon: cat.icon || '', is_active: cat.is_active, sort_order: cat.sort_order });
    setShowAdd(false);
  };

  const handleDelete = async () => {
    try {
      await deleteCategory(deleteId);
      setCategories(prev => prev.filter(c => c.id !== deleteId));
      toast.success('Category deleted');
      setDeleteId(null);
    } catch { toast.error('Failed to delete'); }
  };

  const FormRow = ({ onClose }) => (
    <tr className="bg-surface-700/50">
      <td className="py-2 px-4" colSpan={2}>
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Category name *" className="input py-1.5 text-sm" autoFocus />
      </td>
      <td className="py-2 px-4">
        <input value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="Icon (e.g. Home)" className="input py-1.5 text-sm" />
      </td>
      <td className="py-2 px-4">
        <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" className="input py-1.5 text-sm" />
      </td>
      <td className="py-2 px-4 text-center">
        <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-primary-500" />
      </td>
      <td className="py-2 px-4">
        <div className="flex items-center justify-end gap-1">
          <button onClick={handleSave} disabled={saving} className="btn-primary py-1 px-2 text-xs"><Check size={12} /></button>
          <button onClick={() => { onClose(); setForm(emptyForm); setEditId(null); }} className="btn-secondary py-1 px-2 text-xs"><X size={12} /></button>
        </div>
      </td>
    </tr>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-white">Categories</h1>
          <p className="text-white/40 text-sm mt-0.5">{categories.length} categories</p>
        </div>
        <button onClick={() => { setShowAdd(true); setEditId(null); setForm(emptyForm); }} className="btn-primary">
          <Plus size={15} /> Add Category
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-white/8">
              <tr>
                <th className="table-header text-left">Name</th>
                <th className="table-header text-left">Slug</th>
                <th className="table-header text-left">Icon</th>
                <th className="table-header text-left hidden md:table-cell">Description</th>
                <th className="table-header text-center">Active</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {showAdd && !editId && <FormRow onClose={() => setShowAdd(false)} />}
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(6)].map((_, j) => <td key={j} className="py-3 px-4"><div className="skeleton h-4 rounded" /></td>)}
                  </tr>
                ))
              ) : categories.length === 0 && !showAdd ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-white/30 text-sm">
                    No categories yet. <button onClick={() => setShowAdd(true)} className="text-primary-400 hover:underline">Add one</button>
                  </td>
                </tr>
              ) : (
                categories.map(cat => (
                  editId === cat.id ? (
                    <FormRow key={cat.id} onClose={() => setEditId(null)} />
                  ) : (
                    <tr key={cat.id} className="hover:bg-white/2 transition-colors">
                      <td className="table-cell font-medium text-white">{cat.name}</td>
                      <td className="table-cell text-white/40 font-mono text-xs">{cat.slug}</td>
                      <td className="table-cell text-white/60">{cat.icon || '—'}</td>
                      <td className="table-cell hidden md:table-cell text-white/40 max-w-xs truncate">{cat.description || '—'}</td>
                      <td className="table-cell text-center">
                        <span className={`w-2 h-2 rounded-full inline-block ${cat.is_active ? 'bg-green-400' : 'bg-white/15'}`} />
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleEdit(cat)} className="btn-ghost p-2"><Edit size={13} /></button>
                          <button onClick={() => setDeleteId(cat.id)} className="btn-ghost p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10"><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  )
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal open={!!deleteId} title="Delete Category" message="Delete this category? Projects in this category won't be deleted but will be uncategorized." onConfirm={handleDelete} onCancel={() => setDeleteId(null)} danger />
    </div>
  );
}